/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { batch, useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import ExerciseFilter from './ExerciseFilter';
import ExerciseMetricsContainers from './ExerciseMetricsContainer';
import SCOverviewChart from './SCOverviewChart';
import ForcePlateDatePicker from '../../../force-plate-dashboard/components/presentational/ForcePlateDatePicker';
import ForcePlateDropDown from '../../../force-plate-dashboard/components/presentational/ForcePlateDropDown';
import Button from '../../../../shared/_TBComponents/Button';
import {
  fetchSCDashData,
  setEndDateCal,
  setEndDateData,
  setIsLegendInfoModalShowing,
  setGroup,
  setSelectedAthleteGroup,
  setSelectedAthletePerformance,
  setStartDateCal,
  setStartDateData,
} from '../../ducks/sportCoachDashboardActions';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import { dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

const DashboardContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 10px;
  box-shadow: ${BoxShadow};
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 15px 15px 7px 15px;
  padding: 16px;
  position: relative;
`;

const DateOptionsContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  .printBtn {
    border: 1px solid #808080;
    border-radius: 50%;
    height: 35px;
    margin-left: 10px;
    padding: 3px;
    width: 35px;
  }
`;

const LegendAndIcon = styled('div')`
  align-items: center;
  display: flex;
  filter: ${(props) => (props.isBlurred ? 'blur(1.2px)' : null)};
  margin-left: 17px;
  width: 230px;

  button {
    background: transparent!important;
  }

  svg {
    border: 1px solid #808080;
    border-radius: 50%;
    height: 20px;
    padding: 3px;
    width: 20px;
  }
`;

const LegendTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  margin-right: 5px;
`;

const MenuOptionsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const ModalMessage = styled('div')`
  font-weight: 300;
`;

const ModalTitle = styled('div')`
  font-weight: bold;
`;

const NoDataModal = styled('div')`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: ${BoxShadow};
  color: #424242;
  font-family: 'Nunito Sans';
  font-size: 19px;
  height: 119px;
  left: 50%;
  margin: -45px 0px 0px -182px;
  padding: 15px;
  position: absolute;
  text-align: center;
  top: 50%;
  width: 400px;
`;

const OptionsHeader = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const OverviewComponent = () => {
  const {
    athleteList,
    cardData,
    chartData,
    dateType,
    endDateCal,
    endDateData,
    exercises,
    group,
    groups,
    maxType,
    selectedAthletePerformance,
    startDateCal,
    startDateData,
  } = useSelector((state) => state.sportCoachDashboard.data);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const { isSCDashDataLoading } = useSelector((state) => state.sportCoachDashboard.ui);

  const dispatch = useDispatch();

  const hasCardData = cardData.length > 0;
  const hasChartData = chartData.length > 0;

  const athleteDropdownList = useMemo(() => {
    if (!hasChartData || athleteList.length === 0) return [];
    return [{ fullName: 'View Group', userId: null }, ...athleteList];
  }, [hasChartData, athleteList]);

  const exerIds = useMemo(
    () => (exercises.length > 0 ? exercises.map((item) => item.id) : []),
    [exercises],
  );

  const setAthlete = (option) => {
    if (option.fullName === 'View Group') {
      // Amplitude tracking
      dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Athlete Picker', 'Athlete selection reset');
      dispatch(setSelectedAthletePerformance(null, false));
    } else {
      // Amplitude tracking
      dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Athlete Picker', 'Athlete selected');
      dispatch(setSelectedAthletePerformance(option, true));
    }
  };

  const setEndDate = (date) => {
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Date Picker', 'End date selected');
    batch(() => {
      dispatch(setEndDateData(moment(date).format('YYYY-MM-DD')));
      dispatch(setEndDateCal(moment(date)));
      dispatch(
        fetchSCDashData(
          currentUser.accountCode,
          startDateData,
          moment(date).format('YYYY-MM-DD'),
          group?.id,
          exerIds,
          dateType,
          maxType,
        ),
      );
    });
  };

  const setSelectedGroup = (option) => {
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Group Picker', 'Group selected');
    batch(() => {
      dispatch(setGroup(option));
      dispatch(setSelectedAthletePerformance(null, false));
      dispatch(setSelectedAthleteGroup(null, false));
      dispatch(
        fetchSCDashData(
          currentUser.accountCode,
          startDateData,
          endDateData,
          option.id,
          exerIds,
          dateType,
          maxType,
        ),
      );
    });
  };

  const setStartDate = (date) => {
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Date Picker', 'Start date selected');
    batch(() => {
      dispatch(setStartDateData(moment(date).format('YYYY-MM-DD')));
      dispatch(setStartDateCal(moment(date)));
      dispatch(
        fetchSCDashData(
          currentUser.accountCode,
          moment(date).format('YYYY-MM-DD'),
          endDateData,
          group?.id,
          exerIds,
          dateType,
          maxType,
        ),
      );
    });
  };

  const handleLegendInfoClick = () => {
    // Amplitude tracking
    dashboardTracker('Sport Coach Dashboard', 'Performance Overview - Key Performance Exercise Legend', 'Legend info button clicked');
    dispatch(setIsLegendInfoModalShowing(true));
  };

  const renderNoDataModal = () => {
    if (hasChartData || isSCDashDataLoading) return null;

    return (
      <NoDataModal>
        <ModalTitle>
          {!group && !hasChartData ? 'Select a Group' : 'No Data Available'}
        </ModalTitle>
        <ModalMessage>
          {!group && !hasChartData
            ? 'Select a group using the dropdown above to get started'
            : 'There is no data available, please adjust your selection above'}
        </ModalMessage>
      </NoDataModal>
    );
  };

  const renderOptionsHeader = () => (
    <OptionsHeader>
      <MenuOptionsContainer>
        <ForcePlateDropDown
          defaultOption={group || {}}
          headerWidthPx={175}
          label='name'
          list={groups}
          placeholder='Select a Group'
          readOnly
          selectItemFunction={setSelectedGroup}
        />
        {hasChartData && (
          <ForcePlateDropDown
            defaultOption={selectedAthletePerformance || {}}
            headerWidthPx={175}
            label='fullName'
            list={athleteDropdownList}
            placeholder='Select an Athlete'
            selectItemFunction={setAthlete}
          />
        )}
        <ExerciseFilter />
      </MenuOptionsContainer>
      <DateOptionsContainer>
        <ForcePlateDatePicker
          endDate={endDateCal.toDate()}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          startDate={startDateCal.toDate()}
        />
      </DateOptionsContainer>
    </OptionsHeader>
  );

  return (
    <>
      <DashboardContainer>
        {renderOptionsHeader()}
        <SCOverviewChart />
        {renderNoDataModal()}
      </DashboardContainer>
      <LegendAndIcon isBlurred={!hasCardData}>
        <LegendTitle>
          Key Performance Exercises
        </LegendTitle>
        <Button
          aria-label='Show key performance exercises info modal'
          bgColor='#00000000'
          disabled={!hasCardData}
          icon='info'
          iconColor='#979797'
          iconOnly
          noBorder
          onClick={handleLegendInfoClick}
          rounded
        />
      </LegendAndIcon>
      <ExerciseMetricsContainers />
    </>
  );
};

export default OverviewComponent;
